/**
 * FL-118: シンプルなメール送信サービス
 * FL-154: デモ体験時の外部サービス無効化対応
 * Edge Function経由でメール送信を行う（キューレスアーキテクチャ）
 */

import { getSupabaseClient } from '../utils/supabase';
import logger from '../utils/logger';
import { demoModeService } from './demoModeService';
import { DemoFeature, ServiceType, EmailMockResponse } from '../types/demoMode';

export interface SendEmailOptions {
  companyId: string;
  to: string[];
  subject: string;
  body: string;
  htmlBody?: string;
  fromEmail?: string;
  replyTo?: string;
  emailType?: 'test' | 'daily_report' | 'notification';
}

export interface EmailSendResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

export class EmailService {
  /**
   * メール送信（Edge Function経由）
   */
  async sendEmail(options: SendEmailOptions): Promise<EmailSendResult> {
    try {
      // FL-154: デモモード制限チェック
      const restriction = demoModeService.checkRestriction(DemoFeature.EMAIL_SEND);
      if (restriction.isRestricted) {
        // デモモード時のモックレスポンス
        const mockResponse = demoModeService.getMockResponse(ServiceType.EMAIL, {
          to: options.to,
          subject: options.subject,
          companyId: options.companyId,
        }) as EmailMockResponse;

        demoModeService.logDemoAction(DemoFeature.EMAIL_SEND, 'sendEmail skipped', {
          to: options.to,
          subject: options.subject,
          emailType: options.emailType,
          companyId: options.companyId,
        });

        return {
          success: true,
          messageId: mockResponse.messageId,
        };
      }

      const supabase = getSupabaseClient();
      if (!supabase) {
        return {
          success: false,
          error:
            'システムエラー: Supabaseクライアントが利用できません。しばらく待ってから再試行してください。',
        };
      }

      // 認証状態確認
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        return {
          success: false,
          error: '認証エラー: ログインが必要です。一度ログアウトしてから再度ログインしてください。',
        };
      }

      logger.info('メール送信開始', {
        companyId: options.companyId,
        toCount: options.to.length,
        emailType: options.emailType,
        subject: options.subject,
      });

      // Edge Function経由でメール送信
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          companyId: options.companyId,
          to: options.to,
          subject: options.subject,
          body: options.body,
          htmlBody: options.htmlBody,
          fromEmail: options.fromEmail || 'noreply@rakuhai-ai.jp',
          replyTo: options.replyTo,
          emailType: options.emailType || 'notification',
        },
      });

      if (error) {
        const errorMessage = this.parseSupabaseError(error);
        logger.error('Edge Function呼び出しエラー', {
          error: error.message,
          companyId: options.companyId,
        });
        return {
          success: false,
          error: errorMessage,
        };
      }

      if (!data) {
        return {
          success: false,
          error:
            'メール送信失敗: サーバーから応答がありませんでした。ネットワーク接続を確認して再試行してください。',
        };
      }

      if (!data.success) {
        const errorMessage = data.error || 'メール送信に失敗しました';
        logger.error('メール送信失敗', { error: errorMessage, companyId: options.companyId });
        return {
          success: false,
          error: `メール送信失敗: ${errorMessage}`,
        };
      }

      logger.info('メール送信成功', {
        companyId: options.companyId,
        messageId: data.messageId,
        emailType: options.emailType,
      });

      return {
        success: true,
        messageId: data.messageId,
      };
    } catch (error) {
      const errorMessage = this.parseGeneralError(error);
      logger.error('メール送信失敗', {
        companyId: options.companyId,
        error: error instanceof Error ? error.message : String(error),
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * テストメール送信
   */
  async sendTestEmail(companyId: string, recipientEmail: string): Promise<EmailSendResult> {
    try {
      // 会社情報取得
      const company = await this.getCompanyInfo(companyId);
      if (!company) {
        throw new Error('会社情報が見つかりません');
      }

      const result = await this.sendEmail({
        companyId,
        to: [recipientEmail],
        subject: '[ラクハイ] 日次レポート送信テスト',
        body: `${company.name} 様

日次レポート機能のテストメールです。

このメールが正常に受信できていれば、日次レポート機能の設定が完了しています。

ラクハイシステム`,
        htmlBody: this.generateTestEmailHTML(company.name, recipientEmail),
        emailType: 'test',
      });

      return result;
    } catch (error) {
      logger.error('テストメール送信失敗', {
        companyId,
        recipientEmail,
        error: error instanceof Error ? error.message : String(error),
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * 日次レポートメール送信
   */
  async sendDailyReportEmail(
    companyId: string,
    recipients: string[],
    reportData: {
      reportDate: string;
      csvFilePath?: string;
      pdfFilePath?: string;
      summary: {
        totalCases: number;
        totalRevenue: number;
        totalDistance: number;
      };
    }
  ): Promise<EmailSendResult> {
    try {
      const company = await this.getCompanyInfo(companyId);
      if (!company) {
        throw new Error('会社情報が見つかりません');
      }

      const subject = `[ラクハイ] 日次レポート - ${reportData.reportDate}`;
      const body = this.generateDailyReportBody(company.name, reportData);
      const htmlBody = this.generateDailyReportHTML(company.name, reportData);

      const result = await this.sendEmail({
        companyId,
        to: recipients,
        subject,
        body,
        htmlBody,
        emailType: 'daily_report',
      });

      return result;
    } catch (error) {
      logger.error('日次レポートメール送信失敗', {
        companyId,
        recipients,
        error: error instanceof Error ? error.message : String(error),
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private async getCompanyInfo(companyId: string): Promise<{ id: string; name: string } | null> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) return null;

      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .eq('id', companyId)
        .single();

      if (error) {
        logger.error('会社情報取得エラー', { companyId, error: error.message });
        return null;
      }

      return data;
    } catch (error) {
      logger.error('会社情報取得失敗', { companyId, error });
      return null;
    }
  }

  private generateTestEmailHTML(companyName: string, recipientEmail: string): string {
    return `
      <!DOCTYPE html>
      <html lang="ja">
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>ラクハイ 日次レポート</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', 'Noto Sans JP', sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            text-align: center;
            padding: 20px 0;
            border-bottom: 2px solid #0066cc;
            margin-bottom: 20px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #0066cc;
            margin-bottom: 5px;
          }
          .content {
            white-space: pre-line;
            margin-bottom: 30px;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #dee2e6;
            text-align: center;
            font-size: 12px;
            color: #6c757d;
          }
          .info {
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 5px;
            padding: 10px;
            margin: 10px 0;
            font-size: 12px;
            color: #6c757d;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">ラクハイ</div>
          <div>AI陸送配車管理システム</div>
        </div>
        
        <div class="content">
          ${companyName} 様

日次レポート機能のテストメールです。

このメールが正常に受信できていれば、日次レポート機能の設定が完了しています。

ラクハイシステム
        </div>

        <div class="info">
          <strong>送信情報:</strong><br>
          • 送信方式: シンプルアーキテクチャ<br>
          • 送信時刻: ${new Date().toLocaleString('ja-JP')}<br>
          • 宛先: ${recipientEmail}<br>
        </div>
        
        <div class="footer">
          <p>このメールは自動送信されています。</p>
          <p>© ラクハイシステム</p>
        </div>
      </body>
      </html>
    `;
  }

  private generateDailyReportBody(
    companyName: string,
    reportData: {
      reportDate: string;
      summary: {
        totalCases: number;
        totalRevenue: number;
        totalDistance: number;
      };
    }
  ): string {
    return `${companyName} 様

${reportData.reportDate}の日次レポートをお送りします。

【概要】
・案件数: ${reportData.summary.totalCases}件
・総売上: ¥${reportData.summary.totalRevenue.toLocaleString()}
・総走行距離: ${reportData.summary.totalDistance}km

詳細は添付のCSV・PDFファイルをご確認ください。

ラクハイシステム`;
  }

  private generateDailyReportHTML(
    companyName: string,
    reportData: {
      reportDate: string;
      summary: {
        totalCases: number;
        totalRevenue: number;
        totalDistance: number;
      };
    }
  ): string {
    return `
      <!DOCTYPE html>
      <html lang="ja">
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>ラクハイ 日次レポート - ${reportData.reportDate}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', 'Noto Sans JP', sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            text-align: center;
            padding: 20px 0;
            border-bottom: 2px solid #0066cc;
            margin-bottom: 20px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #0066cc;
            margin-bottom: 5px;
          }
          .content {
            margin-bottom: 30px;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .summary-table th,
          .summary-table td {
            border: 1px solid #dee2e6;
            padding: 12px;
            text-align: left;
          }
          .summary-table th {
            background-color: #f8f9fa;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #dee2e6;
            text-align: center;
            font-size: 12px;
            color: #6c757d;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">ラクハイ</div>
          <div>AI陸送配車管理システム</div>
        </div>
        
        <div class="content">
          <h2>${companyName} 様</h2>
          <p>${reportData.reportDate}の日次レポートをお送りします。</p>

          <table class="summary-table">
            <thead>
              <tr>
                <th>項目</th>
                <th>値</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>案件数</td>
                <td>${reportData.summary.totalCases}件</td>
              </tr>
              <tr>
                <td>総売上</td>
                <td>¥${reportData.summary.totalRevenue.toLocaleString()}</td>
              </tr>
              <tr>
                <td>総走行距離</td>
                <td>${reportData.summary.totalDistance}km</td>
              </tr>
            </tbody>
          </table>

          <p>詳細は添付のCSV・PDFファイルをご確認ください。</p>
        </div>
        
        <div class="footer">
          <p>このメールは自動送信されています。</p>
          <p>© ラクハイシステム</p>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Supabaseエラーを解析して分かりやすいメッセージに変換
   */
  private parseSupabaseError(error: any): string {
    const errorMessage = error.message || String(error);

    // 404エラー（Edge Function未デプロイ）
    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      return 'システムエラー: メール送信機能が利用できません。システム管理者にお問い合わせください。';
    }

    // CORS エラー
    if (errorMessage.includes('CORS') || errorMessage.includes('blocked by CORS policy')) {
      return 'ネットワークエラー: メール送信がブロックされました。ページをリロードして再試行してください。';
    }

    // 認証エラー
    if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
      return '認証エラー: セッションが期限切れです。一度ログアウトして再度ログインしてください。';
    }

    // 権限エラー
    if (errorMessage.includes('forbidden') || errorMessage.includes('403')) {
      return '権限エラー: メール送信権限がありません。管理者にお問い合わせください。';
    }

    // タイムアウトエラー
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      return 'タイムアウトエラー: サーバーの応答に時間がかかっています。しばらく待ってから再試行してください。';
    }

    // ネットワークエラー
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('fetch')
    ) {
      return 'ネットワークエラー: インターネット接続を確認して再試行してください。';
    }

    // サーバーエラー
    if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
      return 'サーバーエラー: 一時的な問題が発生しています。しばらく待ってから再試行してください。';
    }

    // その他のエラー
    return `メール送信エラー: ${errorMessage}`;
  }

  /**
   * 一般的なエラーを解析して分かりやすいメッセージに変換
   */
  private parseGeneralError(error: unknown): string {
    if (error instanceof TypeError) {
      if (error.message.includes('fetch')) {
        return 'ネットワークエラー: インターネット接続を確認して再試行してください。';
      }
      return 'システムエラー: データ処理中に問題が発生しました。再試行してください。';
    }

    if (error instanceof Error) {
      const errorMessage = error.message;

      if (errorMessage.includes('AbortError')) {
        return 'タイムアウトエラー: 処理時間が長すぎます。しばらく待ってから再試行してください。';
      }

      if (errorMessage.includes('Supabase')) {
        return 'データベース接続エラー: システムとの通信に問題があります。再試行してください。';
      }

      return `エラー: ${errorMessage}`;
    }

    return '予期しないエラーが発生しました。再試行してください。';
  }
}

// シングルトンインスタンス
export const emailService = new EmailService();
